import * as React from 'react';

import { useTiptapEditor } from '../../hooks/use-tiptap-editor';
import { useUiEditorState } from '../../hooks/use-ui-editor-state';
import { useCommentAuthor } from '../../../../ui/tiptap/extensions/comment-mark';
import { FloatingElement } from '../../../../ui/tiptap/tiptap-ui-utils/floating-element';
import { commentBelowFloatingOptions } from './comment-anchor';
import { useCommentThreadActions } from './comment-thread-actions';
import { CommentComposer } from './comment-composer';

/**
 * Popover for composing a brand-new comment on the current selection. Anchored to the
 * selection via {@link FloatingElement}; visibility is driven by the shared `uiState`
 * `commentInputVisible` flag (toggled from the floating-toolbar comment button).
 */
export function CommentInputPopover() {
  const { editor } = useTiptapEditor();
  const author = useCommentAuthor();
  const { commentInputVisible } = useUiEditorState(editor);
  const actions = useCommentThreadActions(editor, author);
  const [body, setBody] = React.useState('');
  const cardRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!commentInputVisible) {
      return;
    }
    setBody('');
  }, [commentInputVisible]);

  // Hold the selection visible for as long as the composer is open. Captured once, on
  // the way in: focus is about to move into the textarea, and from then on the
  // editor's own selection is no longer what the user is pointing at.
  React.useEffect(() => {
    if (editor === null) {
      return;
    }
    if (!commentInputVisible) {
      editor.commands.setCommentDraftRange(null);
      return;
    }
    const { from, to } = editor.state.selection;
    editor.commands.setCommentDraftRange(from === to ? null : { from, to });
    return () => {
      editor.commands.setCommentDraftRange(null);
    };
  }, [commentInputVisible, editor]);

  const close = React.useCallback(() => {
    editor?.commands.commentInputHide();
  }, [editor]);

  /**
   * Anywhere else is the way out — there is no Cancel button.
   *
   * `FloatingElement`'s own dismiss deliberately ignores presses that land inside the
   * editor, which is most of what is around this box: the prose it is attached to.
   * Discarding an unsent note is the right reading of clicking back into the text, so
   * this listens for the press itself rather than widening that shared rule for every
   * floating element in the editor.
   *
   * `pointerdown`, not `click`: a press that starts outside and drags into a text
   * selection never produces a click, and the box would be left open over prose the
   * selection has moved away from.
   */
  React.useEffect(() => {
    if (!commentInputVisible) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (
        target instanceof Node &&
        cardRef.current !== null &&
        cardRef.current.contains(target)
      ) {
        return;
      }
      close();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', onPointerDown, true);
  }, [close, commentInputVisible]);

  const submit = React.useCallback(() => {
    if (editor === null) {
      return;
    }
    // Same mutation the thread card's reply runs — see `useCommentThreadActions`. The
    // focus call is this surface's own: the toolbar button that opened this popover
    // took focus out of the prose, and the caret has to go back before the new thread
    // can become the active one.
    editor.commands.focus();
    if (!actions.create(body)) {
      return;
    }
    editor.commands.commentInputHide();
    setBody('');
  }, [actions, body, editor]);

  if (editor === null || author === null || !commentInputVisible) {
    return null;
  }

  return (
    <FloatingElement
      shouldShow={true}
      zIndex={60}
      floatingOptions={commentBelowFloatingOptions}
    >
      <div ref={cardRef} className="w-96 max-w-[90vw]">
        <CommentComposer
          value={body}
          onChange={setBody}
          onSubmit={submit}
          onCancel={close}
          placeholder="Add a comment..."
          submitLabel="Add comment"
          author={author}
          autoFocus
          className="shadow-lg"
        />
      </div>
    </FloatingElement>
  );
}
